import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import websocketManager, { WS_MESSAGE_TYPES } from '../utils/websocketManager';
import chatApi from '../services/chatApi';

// 创建聊天上下文
const ChatContext = createContext();

// 聊天上下文提供者组件
export const ChatProvider = ({ children }) => {
  const [sessions, setSessions] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [wsConnected, setWsConnected] = useState(false);
  const [pendingMessages, setPendingMessages] = useState({});

  // 加载会话列表
  const loadSessions = useCallback(async (keyword = '', sessionType = '') => {
    setIsLoading(true);
    try {
      const response = await chatApi.getSessions(1, 20, keyword, sessionType);
      if (response.success) {
        setSessions(response.data.records);
      }
    } catch (error) {
      console.error('加载会话列表失败:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // 处理聊天消息
  const handleChatMessage = useCallback((data) => {
    if (!data) return;

    try {
      // 更新会话列表中的最后一条消息
      setSessions(prevSessions => {
        if (!Array.isArray(prevSessions)) return [];

        const updatedSessions = prevSessions.map(session => {
          if (session && session.sessionId === data.sessionId) {
            return {
              ...session,
              lastMessageContent: data.content || '',
              lastMessageTime: data.createdAt || new Date().toISOString(),
              unreadCount: currentSession && currentSession.sessionId === data.sessionId ? 0 : (session.unreadCount || 0) + 1
            };
          }
          return session;
        });

        // 将有新消息的会话移到顶部
        const sessionIndex = updatedSessions.findIndex(s => s && s.sessionId === data.sessionId);
        if (sessionIndex > 0) {
          const [movedSession] = updatedSessions.splice(sessionIndex, 1);
          updatedSessions.unshift(movedSession);
        }

        return updatedSessions;
      });

      // 如果是当前会话的消息，添加到消息列表
      if (currentSession && data.sessionId === currentSession.sessionId) {
        setMessages(prevMessages => {
          if (!Array.isArray(prevMessages)) return [];

          // 确保sender对象存在
          const sender = data.sender || {};
          
          // 将新消息添加到消息列表末尾
          const newMessage = {
            messageId: data.messageId || 0,
            senderId: sender.userId || 0,
            senderNickname: sender.nickname || '用户',
            senderAvatar: sender.avatarUrl || 'https://via.placeholder.com/36',
            messageType: data.messageType || 1,
            content: data.content || '',
            recalled: 0,
            createdAt: data.createdAt || new Date().toISOString()
          };
          
          return [...prevMessages, newMessage];
        });
      }
    } catch (error) {
      console.error('处理聊天消息失败:', error);
    }
  }, [currentSession]);

  // 处理消息确认回执
  const handleMessageAck = useCallback((data) => {
    if (!data) return;

    try {
      const { clientMessageId, messageId, createdAt } = data;
      
      if (!clientMessageId) return;

      // 更新待处理消息状态
      setPendingMessages(prev => {
        if (!prev) return {};
        
        const updated = { ...prev };
        if (clientMessageId in updated) {
          delete updated[clientMessageId];
        }
        return updated;
      });

      // 更新消息列表中的临时消息
      setMessages(prevMessages => {
        if (!Array.isArray(prevMessages)) return [];
        
        return prevMessages.map(msg => {
          if (msg && msg.clientMessageId === clientMessageId) {
            return {
              ...msg,
              messageId: messageId || msg.messageId || 0,
              createdAt: createdAt || msg.createdAt || new Date().toISOString(),
              pending: false
            };
          }
          return msg;
        });
      });
    } catch (error) {
      console.error('处理消息确认回执失败:', error);
    }
  }, []);

  // 初始化WebSocket连接
  useEffect(() => {
    const token = localStorage.getItem('satoken');
    if (token) {
      websocketManager.connect(token);
    }

    // 监听WebSocket连接状态
    const handleConnectionChange = (isConnected) => {
      setWsConnected(isConnected);
    };

    // 监听WebSocket消息
    const handleMessage = (message) => {
      if (message.type === WS_MESSAGE_TYPES.CHAT_MESSAGE) {
        handleChatMessage(message.data);
      } else if (message.type === WS_MESSAGE_TYPES.MESSAGE_ACK) {
        handleMessageAck(message.data);
      } else if (message.type === WS_MESSAGE_TYPES.SESSION_UPDATE) {
        // 当WebSocket连接成功或收到会话更新消息时，刷新会话列表
        loadSessions();
      }
    };

    websocketManager.addConnectionListener(handleConnectionChange);
    websocketManager.addMessageListener(handleMessage);

    return () => {
      websocketManager.removeConnectionListener(handleConnectionChange);
      websocketManager.removeMessageListener(handleMessage);
    };
  }, [handleChatMessage, handleMessageAck, loadSessions]);

  // 加载历史消息
  const loadHistoryMessages = useCallback(async (sessionId, cursor = null) => {
    if (!sessionId) return;

    setIsLoading(true);
    try {
      const response = await chatApi.getHistoryMessages(sessionId, cursor);
      if (response.success) {
        const { records, nextCursor, hasMore } = response.data;
        
        // 按时间排序消息，确保从旧到新排列
        // 如果时间相同，则按照messageId排序（较小的ID在前，因为ID是递增的）
        const sortedRecords = [...records].sort((a, b) => {
          const timeA = new Date(a.createdAt).getTime();
          const timeB = new Date(b.createdAt).getTime();
          
          // 首先按时间排序
          if (timeA !== timeB) {
            return timeA - timeB;
          }
          
          // 时间相同时，按messageId排序
          // 将messageId转为字符串进行比较，避免大整数精度问题
          const idA = String(a.messageId);
          const idB = String(b.messageId);
          
          // 数值比较，较小的ID在前
          if (idA.length !== idB.length) {
            return idA.length - idB.length; // 短的ID数值较小
          }
          
          // 长度相同，按字典序比较
          return idA.localeCompare(idB);
        });
        
        // 如果是首次加载，直接使用排序后的记录
        // 如果是加载更多，将新记录添加到现有消息的前面
        setMessages(prev => {
          if (!cursor) {
            return sortedRecords;
          } else {
            // 将新加载的消息添加到现有消息的前面
            return [...sortedRecords, ...prev];
          }
        });
        
        // 保存下一页的游标，确保作为字符串处理
        if (nextCursor !== null && nextCursor !== undefined) {
          const nextCursorStr = String(nextCursor);
          setNextCursor(nextCursorStr);
        } else {
          setNextCursor(null);
        }
        
        setHasMore(hasMore);

        // 如果是初次加载，将会话的未读数重置为0
        if (!cursor) {
          setSessions(prevSessions => {
            return prevSessions.map(session => {
              if (session.sessionId === sessionId) {
                return { ...session, unreadCount: 0 };
              }
              return session;
            });
          });
        }
      }
    } catch (error) {
      console.error('加载历史消息失败:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // 加载更多历史消息
  const loadMoreMessages = useCallback(() => {
    if (hasMore && currentSession && !isLoading && nextCursor) {
      loadHistoryMessages(currentSession.sessionId, nextCursor);
    }
  }, [hasMore, currentSession, nextCursor, isLoading, loadHistoryMessages]);

  // 发送消息
  const sendMessage = useCallback((content, messageType = 1) => {
    if (!currentSession || !content.trim()) return false;

    const clientMessageId = uuidv4();
    const timestamp = new Date().toISOString();
    
    // 安全获取用户信息
    const userInfoStr = localStorage.getItem('userInfo');
    let userInfo = { id: 0, nickname: '用户', avatar: '' };
    
    if (userInfoStr) {
      try {
        const parsedInfo = JSON.parse(userInfoStr);
        userInfo = {
          id: parsedInfo.id || 0,
          nickname: parsedInfo.nickname || parsedInfo.nickName || parsedInfo.userAccount || '用户',
          avatar: parsedInfo.avatar || parsedInfo.avatarUrl || 'https://via.placeholder.com/36'
        };
        
        // 确保用户信息存在
        if (!userInfo.nickname || userInfo.nickname === '用户') {
          // 如果有其他可用信息，可以用作备用
          userInfo.nickname = parsedInfo.username || parsedInfo.userAccount || '用户';
        }
        
        if (!userInfo.avatar || userInfo.avatar === 'https://via.placeholder.com/36') {
          userInfo.avatar = parsedInfo.avatarUrl || parsedInfo.avatar || 'https://via.placeholder.com/36';
        }
      } catch (error) {
        console.error('解析用户信息失败:', error);
      }
    }
    
    // 创建临时消息对象
    const tempMessage = {
      clientMessageId,
      senderId: userInfo.id,
      senderNickname: userInfo.nickname,
      senderAvatar: userInfo.avatar || 'https://via.placeholder.com/36',
      messageType,
      content,
      recalled: 0,
      createdAt: timestamp,
      pending: true
    };

    // 添加到消息列表末尾
    setMessages(prev => [...prev, tempMessage]);

    // 添加到待处理消息
    setPendingMessages(prev => ({
      ...prev,
      [clientMessageId]: tempMessage
    }));

    // 更新会话列表中的最后一条消息
    setSessions(prevSessions => {
      const updatedSessions = prevSessions.map(session => {
        if (session.sessionId === currentSession.sessionId) {
          return {
            ...session,
            lastMessageContent: content,
            lastMessageTime: timestamp
          };
        }
        return session;
      });

      // 将当前会话移到顶部
      const sessionIndex = updatedSessions.findIndex(s => s.sessionId === currentSession.sessionId);
      if (sessionIndex > 0) {
        const [movedSession] = updatedSessions.splice(sessionIndex, 1);
        updatedSessions.unshift(movedSession);
      }

      return updatedSessions;
    });

    // 通过WebSocket发送消息
    const success = websocketManager.sendMessage({
      type: 'chat_message',
      data: {
        sessionId: currentSession.sessionId,
        messageType,
        content,
        clientMessageId,
        // 添加发送者信息，确保后端能够正确处理
        sender: {
          userId: userInfo.id,
          nickname: userInfo.nickname,
          avatarUrl: userInfo.avatar
        }
      }
    });

    return success;
  }, [currentSession]);

  // 查找或创建单聊会话
  const findSingleSession = useCallback(async (userId) => {
    try {
      const response = await chatApi.findSingleSession(userId);
      if (response.success) {
        const newSession = response.data;
        
        // 检查会话是否已存在于列表中
        const existingSession = sessions.find(s => s.sessionId === newSession.sessionId);
        if (!existingSession) {
          setSessions(prev => [
            {
              ...newSession,
              lastMessageContent: '',
              lastMessageTime: new Date().toISOString(),
              unreadCount: 0
            },
            ...prev
          ]);
        }
        
        return newSession;
      }
    } catch (error) {
      console.error('查找或创建单聊会话失败:', error);
      return null;
    }
  }, [sessions]);

  // 切换当前会话
  const switchSession = useCallback(async (sessionId) => {
    if (!sessionId) return;

    try {
      const session = sessions.find(s => s && s.sessionId === sessionId);
      if (session) {
        setCurrentSession(session);
        setMessages([]);
        setNextCursor(null);
        setHasMore(false);
        await loadHistoryMessages(sessionId);
      }
    } catch (error) {
      console.error('切换会话失败:', error);
    }
  }, [sessions, loadHistoryMessages]);

  // 提供的上下文值
  const contextValue = {
    sessions,
    currentSession,
    messages,
    isLoading,
    hasMore,
    wsConnected,
    pendingMessages,
    loadSessions,
    loadHistoryMessages,
    loadMoreMessages,
    sendMessage,
    findSingleSession,
    switchSession
  };

  return (
    <ChatContext.Provider value={contextValue}>
      {children}
    </ChatContext.Provider>
  );
};

// 自定义钩子，用于在组件中访问聊天上下文
export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat必须在ChatProvider内部使用');
  }
  return context;
};

export default ChatContext; 